import { useMemo, useState } from "react";
import { X, Trash2, Ban, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Badge, Label, sel, SlotPicker, TYPE_META, STATUS_META, SHOOTING_STATUS_META, fmtD, fmtDur,
} from "./shared";

/* Fiche shooting : contenus à tourner regroupés par événement, case « Tourné » distincte de « créé » */
export function ShootingDrawer({ shooting, clientName, onClose, onChanged }) {
  const [f, setF] = useState({
    date: shooting.date || "", time: shooting.time || "",
    duration_min: shooting.duration_min || 120, location: shooting.location || "",
    capacity: shooting.capacity || "", status: shooting.status || "planifie",
    participants: shooting.participants || "", notes: shooting.notes || "",
  });
  const [items, setItems] = useState(shooting.items || []);
  const [busy, setBusy] = useState(false);

  const groups = useMemo(() => {
    const g = {};
    for (const i of items) {
      const k = i.highlight_name || "Sans temps fort";
      (g[k] = g[k] || []).push(i);
    }
    return Object.entries(g);
  }, [items]);

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setBusy(true);
    await act(() => api.patch(`/content-piloting/shootings/${shooting.id}`, {
      ...f, capacity: f.capacity || null, duration_min: Number(f.duration_min) || 120,
    }), "Shooting mis à jour — agenda synchronisé");
    setBusy(false);
  };
  const toggleShot = (item, shot) => act(async () => {
    await api.patch(`/content-piloting/items/${item.id}`, { shot });
    setItems((list) => list.map((x) => (x.id === item.id ? { ...x, shot } : x)));
  }, shot ? "Marqué comme tourné" : "Coche retirée");
  const detach = (item) => act(async () => {
    await api.patch(`/content-piloting/items/${item.id}`, { shooting_id: null });
    setItems((list) => list.filter((x) => x.id !== item.id));
  }, "Contenu retiré du shooting");
  const cancel = () => {
    if (!window.confirm("Annuler ce shooting ? Le créneau sera libéré dans l'agenda et les contenus détachés resteront à programmer.")) return;
    act(() => api.patch(`/content-piloting/shootings/${shooting.id}`, { status: "annule" }), "Shooting annulé — créneau libéré")
      .then(() => onClose());
  };
  const remove = () => {
    if (!window.confirm("Supprimer définitivement ce shooting ?")) return;
    act(() => api.delete(`/content-piloting/shootings/${shooting.id}`), "Shooting supprimé").then(() => onClose());
  };

  const shotCount = items.filter((i) => i.shot).length;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-5 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="sh-drawer">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-bold text-lg" data-testid="sh-drawer-title">Shooting — {clientName}</h3>
            <p className="text-[11px] text-neutral-400">{fmtD(shooting.date)}{shooting.time ? ` à ${shooting.time}` : ""} · {shotCount}/{items.length} tournés</p>
          </div>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid="sh-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="sh-date" /></div>
          <div><Label>Heure</Label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="sh-time" /></div>
          <div><Label>Durée prévue</Label>
            <select className={sel} value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: Number(e.target.value) })} data-testid="sh-duration">
              {[60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{fmtDur(m)}</option>)}
            </select></div>
          <div><Label>Capacité max (contenus)</Label><Input type="number" min="0" value={f.capacity} placeholder="Illimitée"
            onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="sh-capacity" /></div>
          <div><Label>Lieu</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="sh-location" /></div>
          <div><Label>Statut</Label>
            <select className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="sh-status">
              {Object.entries(SHOOTING_STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select></div>
        </div>
        <SlotPicker endpoint="/content-piloting/shooting-slots" durationMin={Number(f.duration_min) || 120}
          picked={f.date && f.time ? { date: f.date, start: f.time } : null}
          onPick={(sl) => setF({ ...f, date: sl.date, time: sl.start })} testid="sh-slot-picker" />
        <div><Label>Participants</Label><Input value={f.participants} onChange={(e) => setF({ ...f, participants: e.target.value })} data-testid="sh-participants" /></div>
        <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="sh-notes" /></div>
        <Button className="w-full bg-[#0B132B] hover:bg-[#141E3C] text-white" disabled={busy} onClick={save} data-testid="sh-save">
          {busy ? "Enregistrement…" : "Enregistrer (vérification de l'agenda)"}
        </Button>

        <div className="border border-neutral-200 rounded-xl p-3 space-y-3" data-testid="sh-items">
          <p className="text-xs font-semibold text-neutral-600">Contenus à tourner — par événement</p>
          {groups.map(([hlName, list]) => (
            <div key={hlName}>
              <p className="text-[10px] font-bold uppercase tracking-wide text-violet-500 mb-1">{hlName}</p>
              <div className="divide-y divide-neutral-100">
                {list.map((i) => (
                  <div key={i.id} className="py-1.5 flex items-center gap-2" data-testid={`sh-item-${i.id}`}>
                    <label className="flex items-center gap-1.5 text-xs text-neutral-600 shrink-0" data-testid={`sh-shot-wrap-${i.id}`}>
                      <input type="checkbox" className="rounded accent-teal-600" checked={!!i.shot}
                        onChange={(e) => toggleShot(i, e.target.checked)} data-testid={`sh-shot-${i.id}`} />
                      Tourné
                    </label>
                    <p className={`text-xs font-medium truncate flex-1 ${i.shot ? "line-through text-neutral-400" : ""}`}>{i.title}</p>
                    {i.created && <span className="text-[9px] font-semibold text-emerald-600 shrink-0">créé ✓</span>}
                    <Badge meta={TYPE_META} value={i.type} />
                    <button className="text-[10px] text-neutral-300 hover:text-red-500 shrink-0" onClick={() => detach(i)} data-testid={`sh-detach-${i.id}`}>retirer</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-[11px] text-neutral-300 text-center py-3">Aucun contenu rattaché — ajoutez-en depuis la fiche d'un événement.</p>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-amber-600" onClick={cancel} data-testid="sh-cancel"
            disabled={f.status === "annule"}>
            <Ban className="w-3.5 h-3.5" /> Annuler le shooting
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-red-500" onClick={remove} data-testid="sh-delete">
            <Trash2 className="w-3.5 h-3.5" /> Supprimer
          </Button>
        </div>
      </div>
    </div>
  );
}
